import { useEffect, useState } from 'react';

const GRID_STORAGE_KEY = 'process-editor:grid-on';
const MINIMAP_STORAGE_KEY = 'process-editor:minimap-on';

/**
 * Read a boolean flag from localStorage with a default. Strings of `'1'`
 * and `'0'` are used so the value is one byte on disk and matches the
 * shape the rest of the editor uses for sessionStorage UI flags.
 */
const readFlag = (key: string, fallback: boolean): boolean => {
  try {
    const raw = globalThis.localStorage.getItem(key);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    /* ignore */
  }
  return fallback;
};

const writeFlag = (key: string, value: boolean): void => {
  try {
    globalThis.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore -- persistence is best-effort */
  }
};

export interface UseCanvasOverlaysReturn {
  readonly gridOn: boolean;
  readonly setGridOn: (next: boolean) => void;
  readonly miniOn: boolean;
  readonly setMiniOn: (next: boolean) => void;
}

/**
 * Persisted on/off flags for the canvas's two visual overlays:
 *   - grid  -> the dotted/lined background pattern
 *   - mini  -> the minimap in the bottom-right
 *
 * Both default to ON because the canvas reads better with both. The
 * bottom toolbar offers a one-click toggle for each so a user that
 * wants a clean canvas (e.g. for a screenshot or a presentation) can
 * suppress them temporarily. The setting persists per browser via
 * {@code localStorage}.
 */
export function useCanvasOverlays(): UseCanvasOverlaysReturn {
  const [gridOn, setGridOn] = useState<boolean>(() =>
    readFlag(GRID_STORAGE_KEY, true),
  );
  const [miniOn, setMiniOn] = useState<boolean>(() =>
    readFlag(MINIMAP_STORAGE_KEY, true),
  );

  useEffect(() => {
    writeFlag(GRID_STORAGE_KEY, gridOn);
  }, [gridOn]);
  useEffect(() => {
    writeFlag(MINIMAP_STORAGE_KEY, miniOn);
  }, [miniOn]);

  return { gridOn, setGridOn, miniOn, setMiniOn };
}
